import {
  getNonDateKeyColumns,
  hasDateKeyColumns,
} from './ReferenceDataSchemaInterface';
import { RawSQL, sqlF, sqlIdentifier, sqlJoinF } from '../sqlTemplate';

/**
 * Returns a SQL fragment for checking if there are duplicate rows in ref data.
 *
 * When run, the SQL groups by the unique keys given, and returns a table with
 * a row for each group. In addition to a column for each unique key, the table
 * also has the following columns:
 *
 *   __rows: a comma-separated string of row numbers belonging to the group.
 *   __count: the number of rows belonging to the group.
 */
export function sqlForDuplicateRows(
  slug: string,
  uniqueKeys: ReadonlyArray<string>
): RawSQL {
  return sqlF`
    SELECT
      ${sqlJoinF(
        uniqueKeys.map((c) => sqlF`"${c}", `),
        ''
      )}
      COUNT(*) AS __count,
      STRING_AGG(__row_number, ', ') AS __rows
    FROM (
      SELECT *, ROW_NUMBER() OVER () AS __row_number FROM "${slug}"
    )
    GROUP BY ALL
    HAVING __count > 1
  `;
}

/**
 * Returns a SQL fragment for checking if there are rows in ref data with date
 * overlap.
 *
 * When run, the SQL does a self-join to find rows that both match in the given
 * unique keys and have overlapping date ranges. It returns a row for each pair
 * with overlaps. In addition to a column for each unique key, the table also
 * has the following columns:
 *
 *   __rows: a comma-separated string of row numbers belonging to the pair.
 *   __count: the number of rows belonging to the group (this is always 2, but
 *            kept for consistency with sqlForDuplicateRows).
 */
export function sqlForDateOverlaps(
  slug: string,
  uniqueKeys: ReadonlyArray<string>
): RawSQL {
  if (!hasDateKeyColumns(uniqueKeys)) {
    return sqlF`SELECT NULL AS error`;
  }

  const uniqueKeysWithoutDates = getNonDateKeyColumns(uniqueKeys);

  // Using a variable for string interpolation instead of a CTE because
  // downstream consumers might want to using this fragment in a subquery,
  // where we can't have CTEs.
  const withRowNumbers = sqlF`
    SELECT
      ${sqlJoinF(
        uniqueKeys.map((x) =>
          ['start_date', 'end_date'].includes(x)
            ? sqlF`DATE_CAST("${x}") as "${x}", `
            : sqlF`"${x}", `
        ),
        ''
      )}
      ROW_NUMBER() OVER () AS __row_number
    FROM ${sqlIdentifier(slug)}
  `;

  // Using b.__row_number > a.__row_number in the join condition allows us to
  // avoid duplicate pairs.
  return sqlF`
    SELECT
      ${sqlJoinF(
        uniqueKeys.map((c) => sqlF`a."${c}", `),
        ''
      )}
      2 as __count,
      LIST_AGGR([a.__row_number, b.__row_number], 'STRING_AGG', ', ') AS __rows
    FROM (${withRowNumbers}) a
    INNER JOIN (${withRowNumbers}) b
      ON (
        b.__row_number > a.__row_number
        ${sqlJoinF(
          uniqueKeysWithoutDates.map((c) => sqlF`AND b."${c}" = a."${c}"`),
          ' '
        )}
        AND (
          (b.start_date >= a.start_date AND b.start_date < a.end_date)
          OR (b.end_date > a.start_date AND b.end_date < a.end_date)
        )
      )
  `;
}
